import React, {Component} from 'react';
import {oneOf, node, string} from 'prop-types';
import styles from './FacebookDiagnostics.scss';
import {equals, indexBy, mergeAll, pipe, prop, tap} from 'ramda';
import classNames from 'classnames';
import {default as Icon} from '@sinclair-digital/sbg-storyline-components/src/components/Icon';
import {TooltipWrapper} from '@sinclair-digital/sbg-storyline-components/src/components/TooltipWrapper';
import {checkCircle} from 'react-icons-kit/fa/checkCircle';
import {exclamationCircle} from 'react-icons-kit/fa/exclamationCircle';
import {questionCircle} from 'react-icons-kit/fa/questionCircle';
import {getSDK} from 'integrations/facebook';

const apiRequestBuilder = FB => (resource, options) => {
    return new Promise((resolve, reject) => {
        FB.api(resource, 'GET', options, ({error, ...rest}) => {
            if (error) {
                return reject(error);
            }
            return resolve(rest);
        });
    });
};

const Badge = ({children, className, type}) => {
    return (
        <span className={classNames(styles.Badge, styles[type], className)} title={children}>
            {type === 'good' && <Icon icon={checkCircle} />}
            {type === 'bad' && <Icon icon={exclamationCircle} />}
            {type === 'incomplete' && <Icon icon={questionCircle} />}
        </span>
    );
};

Badge.propTypes = {
    type: oneOf(['good', 'incomplete', 'bad']),
    className: string,
    children: node,
};

function bootstrapApi(applicationId) {
    const consolidatedResponse = [];
    const addResponse = response => {
        consolidatedResponse.push(response);
        return response;
    };
    const mergeResponses = () => mergeAll(consolidatedResponse);
    return new Promise(resolve => {
        const timer = setTimeout(function() {
            resolve(mergeResponses());
        }, 2000);

        const getLoginStatusCallback = loginStatusResponse => {
            const {status, authResponse} = loginStatusResponse;
            return {status, ...authResponse};
        };

        getSDK(applicationId)
            .then(tap(FB => addResponse({FB, facebookRequestedStarted: true})))
            .then(
                FB =>
                    new Promise(_resolve =>
                        FB.getLoginStatus(
                            pipe(
                                getLoginStatusCallback,
                                _resolve,
                            ),
                            true,
                        ),
                    ),
            )
            .then(addResponse, addResponse)
            .then(() => {
                clearTimeout(timer);
                return mergeResponses();
            })
            .then(resolve);
    });
}

export class FacebookDiagnostics extends Component {
    static propTypes = {
        applicationId: string,
        pageId: string,
    };

    static defaultProps = {
        applicationId: '',
        pageId: '',
    };

    constructor(props) {
        super(props);
        this.state = {
            status: '',
            expiresIn: -1,
            userID: '',
            pages: {},
            facebookRequestedStarted: false,
        };
    }

    bootstrap = applicationId => {
        const bootstrapResponse = bootstrapApi(applicationId).then(response => this.setState(response));
        return bootstrapResponse;
    };

    componentDidMount() {
        const {applicationId} = this.props;
        applicationId && this.bootstrap(applicationId);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !equals(nextProps, this.props) || !equals(nextState, this.state);
    }

    componentWillReceiveProps(nextProps) {
        const {applicationId} = nextProps.applicationId;
        if (this.props.applicationId !== applicationId) {
            // application id was updated.
            this.setState({
                status: '',
                expiresIn: -1,
                userID: '',
                pages: {},
                facebookRequestedStarted: false,
            });
            this.bootstrap(applicationId);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {applicationId} = this.props;
        const {FB, status, userID} = this.state;

        if (prevState.status !== 'connected' && status === 'connected') {
            const builder = apiRequestBuilder(FB);
            Promise.all([builder('/me'), builder(`/${userID}/accounts`, {limit: '1000'})]).then(
                ([meResponse, meAccountsResponse]) => {
                    this.setState({
                        name: meResponse.name,
                        pages: indexBy(prop('id'), meAccountsResponse.data),
                    });
                },
            );
        } else if (status === '' && this.state.facebookRequestedStarted === false) {
            // have not received a status
            this.bootstrap(applicationId);
        }
    }

    render() {
        const {applicationId, pageId} = this.props;
        const {name, pages, status} = this.state;

        if (status === '') {
            return (
                <TooltipWrapper position="top" tooltip="Invalid LiveStream Facebook App ID">
                    <Badge type="bad" calssName={styles.invalidAppId} />
                </TooltipWrapper>
            );
        }

        if (status === 'not_authorized') {
            return (
                <TooltipWrapper position="top" tooltip="Must log into Facebook from Storyline">
                    <Badge type="incomplete" className={styles.noPageId} />
                </TooltipWrapper>
            );
        }

        if (status !== 'connected') {
            // user is not signed in, should not show details
            return (
                <TooltipWrapper position="top" tooltip="Not logged into Facebook">
                    <Badge type="incomplete" className={styles.notSignedIn} />
                </TooltipWrapper>
            );
        }
        if (!pageId) {
            return (
                <TooltipWrapper position="top" tooltip="No Facebook Page ID provided">
                    <Badge type="incomplete" className={styles.noPageId} />
                </TooltipWrapper>
            );
        }
        if (!applicationId) {
            return (
                <TooltipWrapper position="top" tooltip="No LiveStream Facebook App ID provided">
                    <Badge type="incomplete" className={styles.noAppId} />
                </TooltipWrapper>
            );
        }

        const page = pages[pageId];

        if (!page) {
            return (
                <TooltipWrapper position="top" tooltip="Logged in user does not have access to Facebook Page ID">
                    <Badge type="bad" className={styles.noPageAccess} />
                </TooltipWrapper>
            );
        }
        if (!page.tasks || !page.tasks.includes('CREATE_CONTENT')) {
            return (
                <TooltipWrapper
                    position="top"
                    tooltip={`Logged in Facebook user '${name}' does not have access to the Facebook Page`}>
                    <Badge type="bad" className={styles.noContentCreateAccess} />
                </TooltipWrapper>
            );
        }
        return (
            <TooltipWrapper
                position="top"
                tooltip={`Facebook user '${name}' is logged in and able to livestream to Facebook Page ${pageId}`}>
                <Badge type="good" className={styles.good} />
            </TooltipWrapper>
        );
    }
}

export default FacebookDiagnostics;
